/**
 * The transport for a game that may speak to the platform itself.
 *
 * Two kinds of game land here: one whose manifest declares
 * `permissions.network.mode = ALLOWLIST` with the platform's https origins, and
 * a game running outside the sandbox during development. Everything else should
 * use the bridge — a `network.mode = NONE` manifest compiles to `connect-src
 * 'none'` and every call in this file would be blocked by the browser.
 *
 * The token travels as a WebSocket subprotocol rather than a query parameter. A
 * browser cannot set headers on a WebSocket handshake, and a query string ends
 * up in access logs and `Referer`; the subprotocol is the one channel that is
 * both browser-reachable and not routinely logged.
 *
 * Reconnection is real here rather than a state label. The previous revision set
 * the state to `RECONNECTING` on close and then did nothing, so a game that lost
 * its socket mid-stream silently stopped receiving actions for the rest of the
 * run while reporting a healthy-looking state.
 */
import { TRYPORTALError, isRetryableStatus } from './errors.js';
import { toLeaderboardPage } from './leaderboard.js';
import { isGameActionEnvelope } from './validate.js';
export const PROTOCOL_VERSION = '1.0';
export const TOKEN_SUBPROTOCOL_PREFIX = 'tryportal.token.';
export const PROTOCOL_SUBPROTOCOL = 'tryportal.v1';
/**
 * Split `<JWS>.<nonce>` at the *last* dot.
 *
 * A JWS is itself three dot-separated parts, so splitting at the first dot would
 * hand the gateway a fragment of a header.
 */
export function splitSessionToken(sessionToken) {
    const lastDot = sessionToken.lastIndexOf('.');
    if (lastDot <= 0 || lastDot === sessionToken.length - 1) {
        throw new TRYPORTALError('INVALID_SESSION_TOKEN', 'session token is not of the documented form <JWS>.<nonce>');
    }
    return { jws: sessionToken.slice(0, lastDot), nonce: sessionToken.slice(lastDot + 1) };
}
function isRecord(value) {
    return typeof value === 'object' && value !== null;
}
export class DirectTransport {
    kind = 'DIRECT';
    session;
    options;
    jws;
    refreshing;
    sessionNonce;
    socket;
    handlers;
    lastServerSequence = null;
    reconnectAttempt = 0;
    closed = false;
    httpConnect;
    httpEndRequested = false;
    constructor(options) {
        this.options = options;
        const split = splitSessionToken(options.sessionToken);
        this.jws = split.jws;
        this.sessionNonce = split.nonce;
        this.session = {
            gameSessionId: options.gameSessionId,
            rankingTrack: options.rankingTrack === 'LIVE_INTERACTIVE' || options.rankingTrack === 'OFFICIAL_EVENT'
                ? options.rankingTrack
                : 'STANDARD',
            competitionId: options.competitionId ?? '',
            liveSessionId: options.liveSessionId ?? null,
        };
    }
    setState(state) {
        this.handlers?.onStateChange(state);
    }
    isCurrentSocket(socket) {
        return !this.closed && this.socket === socket;
    }
    get sleep() {
        return (this.options.sleep ??
            ((ms) => new Promise((resolve) => {
                setTimeout(resolve, ms);
            })));
    }
    async start(handlers) {
        this.handlers = handlers;
        // These server-owned tracks have no viewer actions. Authenticate once over
        // HTTP instead of keeping a socket and heartbeat loop alive per player.
        // Launches without track metadata retain the legacy WebSocket path.
        if (this.options.rankingTrack === 'STANDARD' ||
            this.options.rankingTrack === 'OFFICIAL_EVENT') {
            await this.startHttpSession();
            return;
        }
        await this.openOnce();
    }
    async startHttpSession() {
        this.setState('CONNECTING');
        const abort = new AbortController();
        this.httpConnect = abort;
        const timeout = setTimeout(() => {
            abort.abort();
        }, this.connectTimeoutMs);
        try {
            const response = await this.authenticatedFetch(`${this.options.apiBaseUrl}/v1/game-sessions/${this.options.gameSessionId}`, {
                method: 'GET',
                headers: { authorization: `Bearer ${this.jws}`, accept: 'application/json' },
                signal: abort.signal,
                cache: 'no-store',
            });
            if (!response.ok)
                throw new TRYPORTALError('CONNECT_FAILED', 'the platform refused this game session', {
                    status: response.status,
                    retryable: isRetryableStatus(response.status),
                });
            const session = await response.json();
            if (!isRecord(session) ||
                session['gameSessionId'] !== this.session.gameSessionId ||
                session['rankingTrack'] !== this.session.rankingTrack ||
                (this.session.competitionId !== '' &&
                    session['competitionId'] !== this.session.competitionId) ||
                session['liveSessionId'] != null)
                throw new TRYPORTALError('INVALID_SESSION_TOKEN', 'the platform session does not match this launch');
            if (!['CREATED', 'ACTIVE'].includes(String(session['state'])))
                throw new TRYPORTALError('SESSION_NOT_ACTIVE', 'this game session is no longer active');
            if (this.closed)
                throw new TRYPORTALError('SDK_CLOSED', 'the SDK was closed during connection');
            this.setState('CONNECTED');
        }
        catch (error) {
            if (!this.closed)
                this.setState('CLOSED');
            if (error instanceof TRYPORTALError)
                throw error;
            throw new TRYPORTALError('CONNECT_FAILED', 'the platform session could not be reached', {
                retryable: true,
                cause: error,
            });
        }
        finally {
            clearTimeout(timeout);
            if (this.httpConnect === abort)
                this.httpConnect = undefined;
        }
    }
    openOnce() {
        const factory = this.options.socketFactory ??
            ((url, protocols) => new WebSocket(url, [...protocols]));
        this.setState(this.reconnectAttempt === 0 ? 'CONNECTING' : 'RECONNECTING');
        const socket = factory(this.options.realtimeUrl, [
            PROTOCOL_SUBPROTOCOL,
            `${TOKEN_SUBPROTOCOL_PREFIX}${this.jws}`,
        ]);
        this.socket = socket;
        return new Promise((resolve, reject) => {
            let settled = false;
            const settle = (fn) => {
                if (settled)
                    return;
                settled = true;
                clearTimeout(timer);
                fn();
            };
            const timer = setTimeout(() => {
                settle(() => {
                    socket.close(4000, 'connect timeout');
                    reject(new TRYPORTALError('CONNECT_FAILED', `the realtime gateway did not send a welcome within ${String(this.connectTimeoutMs)}ms`, { retryable: true }));
                });
            }, this.connectTimeoutMs);
            // Not `unref`ed: see bootstrap.ts. Cleared by `settle` on every path.
            socket.addEventListener('open', () => {
                if (this.closed || this.socket !== socket)
                    return;
                socket.send(JSON.stringify({
                    schemaVersion: 1,
                    messageType: 'CLIENT_HELLO',
                    protocolVersion: PROTOCOL_VERSION,
                    gameSessionId: this.options.gameSessionId,
                    sdkVersion: this.options.sdkVersion,
                    lastAckedServerSequence: this.lastServerSequence,
                    clientInstanceId: this.options.newClientInstanceId(),
                }));
            });
            socket.addEventListener('message', (event) => {
                void this.handleFrame(socket, event.data, () => {
                    settle(() => {
                        this.reconnectAttempt = 0;
                        this.setState('CONNECTED');
                        resolve();
                    });
                });
            });
            socket.addEventListener('close', (event) => {
                const wasCurrent = this.socket === socket;
                if (wasCurrent)
                    this.socket = undefined;
                settle(() => {
                    reject(new TRYPORTALError('CONNECT_FAILED', `realtime connection closed before welcome: ${String(event.code)} ${event.reason}`, { retryable: true }));
                });
                if (wasCurrent && !this.closed)
                    void this.scheduleReconnect();
            });
            socket.addEventListener('error', (error) => {
                if (this.closed || this.socket !== socket)
                    return;
                this.handlers?.log('realtime socket error', error);
                this.setState('DEGRADED');
            });
        });
    }
    get connectTimeoutMs() {
        return this.options.connectTimeoutMs ?? 10_000;
    }
    /**
     * Exponential backoff with full jitter.
     *
     * Full jitter rather than a fixed ramp because every game in a live session
     * loses its socket at the same instant when the gateway restarts, and a
     * deterministic delay would bring them all back simultaneously.
     */
    async scheduleReconnect() {
        const maxAttempts = this.options.maxReconnectAttempts ?? 6;
        if (maxAttempts === 0) {
            this.setState('CLOSED');
            return;
        }
        if (this.reconnectAttempt >= maxAttempts) {
            this.handlers?.log('giving up on reconnection', { attempts: this.reconnectAttempt });
            this.setState('CLOSED');
            return;
        }
        this.reconnectAttempt += 1;
        this.setState('RECONNECTING');
        const base = this.options.reconnectBaseDelayMs ?? 500;
        const max = this.options.reconnectMaxDelayMs ?? 15_000;
        const ceiling = Math.min(max, base * 2 ** (this.reconnectAttempt - 1));
        const random = this.options.random ?? Math.random;
        await this.sleep(Math.floor(random() * ceiling));
        if (this.closed)
            return;
        try {
            await this.openOnce();
        }
        catch (error) {
            this.handlers?.log('reconnect attempt failed', error);
        }
    }
    async handleFrame(source, data, onWelcome) {
        if (!this.isCurrentSocket(source))
            return;
        let parsed;
        try {
            parsed = JSON.parse(typeof data === 'string' ? data : String(data));
        }
        catch {
            this.handlers?.log('discarded a frame that was not JSON');
            return;
        }
        if (!isRecord(parsed))
            return;
        if (typeof parsed['sequence'] === 'number') {
            this.lastServerSequence = parsed['sequence'];
        }
        switch (parsed['messageType']) {
            case 'SERVER_WELCOME':
                onWelcome();
                return;
            case 'HEARTBEAT':
                source.send(JSON.stringify({
                    schemaVersion: 1,
                    messageType: 'HEARTBEAT',
                    sentAt: new Date().toISOString(),
                    sequence: typeof parsed['sequence'] === 'number' ? parsed['sequence'] : 0,
                }));
                return;
            case 'SESSION_STATE': {
                const state = typeof parsed['state'] === 'string' ? parsed['state'] : 'UNKNOWN';
                const killSwitchActive = parsed['killSwitchActive'] === true;
                this.handlers?.onSessionState(state, killSwitchActive, typeof parsed['reason'] === 'string' ? parsed['reason'] : 'session state changed');
                return;
            }
            case 'ERROR':
                this.handlers?.log('realtime protocol error', parsed);
                this.setState('DEGRADED');
                return;
            case 'ACTION': {
                if (!isGameActionEnvelope(parsed)) {
                    this.handlers?.log('discarded an action that failed contract validation');
                    return;
                }
                if (parsed.gameSessionId !== this.options.gameSessionId) {
                    this.handlers?.log('discarded an action addressed to another session');
                    return;
                }
                const handlers = this.handlers;
                if (handlers === undefined)
                    return;
                const ack = await handlers.onAction(parsed);
                // The result belongs to this delivery's transport. A handler may finish
                // after reconnect; writing it to the replacement socket would pay that
                // connection's ACK credit for a frame it never received.
                if (this.isCurrentSocket(source))
                    source.send(JSON.stringify(ack));
                return;
            }
            default:
                return;
        }
    }
    get fetchImpl() {
        const supplied = this.options.fetchImpl;
        if (supplied !== undefined)
            return supplied;
        if (typeof fetch !== 'function') {
            throw new TRYPORTALError('INVALID_USAGE', 'no fetch implementation is available; pass fetchImpl explicitly');
        }
        // Calling the getter result as this.fetchImpl otherwise gives native
        // Window.fetch this transport as its receiver and throws Illegal invocation.
        return globalThis.fetch.bind(globalThis);
    }
    /** Refresh once on 401, sharing one refresh across concurrent score/share calls. */
    async authenticatedFetch(url, init) {
        const sentToken = this.jws;
        const send = () => this.fetchImpl(url, {
            ...init,
            credentials: 'omit',
            headers: {
                ...init['headers'],
                authorization: `Bearer ${this.jws}`,
            },
        });
        let response = await send();
        if (response.status !== 401 || !this.options.refreshToken)
            return response;
        if (this.jws === sentToken) {
            this.refreshing ??= (async () => {
                const renewed = await this.fetchImpl(`${this.options.apiBaseUrl}/v1/game-sessions/${this.options.gameSessionId}/sdk-token`, {
                    method: 'POST',
                    credentials: 'omit',
                    signal: AbortSignal.timeout(this.connectTimeoutMs),
                    headers: {
                        'content-type': 'application/json',
                        authorization: `Bearer ${this.options.refreshToken}`,
                        'idempotency-key': this.options.newClientInstanceId(),
                    },
                    body: '{}',
                });
                if (!renewed.ok)
                    throw new TRYPORTALError('CONNECT_FAILED', 'Reconnect this game to TRAIDENT.', {
                        status: renewed.status,
                    });
                const body = await renewed.json();
                if (!isRecord(body) || typeof body['bearerToken'] !== 'string')
                    throw new TRYPORTALError('INVALID_SESSION_TOKEN', 'invalid refreshed token');
                this.jws = body['bearerToken'];
            })().finally(() => {
                this.refreshing = undefined;
            });
            await this.refreshing;
        }
        response = await send();
        return response;
    }
    /**
     * POST the submission, replaying it on transport-shaped failures.
     *
     * Every attempt carries the same `Idempotency-Key`, so a replay after a
     * timeout cannot produce a second score row — which is what makes retrying
     * safe enough to do automatically.
     */
    async submitScore(request) {
        const attempts = Math.max(1, this.options.scoreRetryAttempts ?? 3);
        let lastError;
        for (let attempt = 1; attempt <= attempts; attempt += 1) {
            try {
                const response = await this.authenticatedFetch(`${this.options.apiBaseUrl}/v1/game-sessions/${this.options.gameSessionId}/scores`, {
                    method: 'POST',
                    headers: {
                        'content-type': 'application/json',
                        authorization: `Bearer ${this.jws}`,
                        'idempotency-key': request.clientSubmissionId,
                    },
                    body: JSON.stringify(request),
                });
                if (response.ok) {
                    const receipt = await response.json();
                    if (!isRecord(receipt) ||
                        typeof receipt['scoreSubmissionId'] !== 'string' ||
                        typeof receipt['verificationStatus'] !== 'string' ||
                        typeof receipt['publicRankEligible'] !== 'boolean' ||
                        typeof receipt['claimRequired'] !== 'boolean' ||
                        receipt['rankingTrack'] !== this.session.rankingTrack) {
                        throw new TRYPORTALError('SCORE_UNREACHABLE', 'the platform returned an invalid score receipt', { retryable: true });
                    }
                    // Core acknowledges the immutable submitted number without echoing it.
                    // Like the hosted bridge, attach that number only after real acceptance.
                    return {
                        scoreSubmissionId: receipt['scoreSubmissionId'],
                        scoreValue: request.scoreValue,
                        verificationStatus: receipt['verificationStatus'],
                        publicRankEligible: receipt['publicRankEligible'],
                        claimRequired: receipt['claimRequired'],
                        rankingTrack: this.session.rankingTrack,
                    };
                }
                const body = await safeText(response);
                if (!isRetryableStatus(response.status)) {
                    throw new TRYPORTALError('SCORE_REJECTED', `the platform rejected this score`, {
                        status: response.status,
                        detail: body,
                    });
                }
                lastError = new TRYPORTALError('SCORE_UNREACHABLE', 'the platform could not be reached', {
                    status: response.status,
                    detail: body,
                    retryable: true,
                });
            }
            catch (error) {
                if (error instanceof TRYPORTALError && !error.retryable)
                    throw error;
                lastError = error;
            }
            if (attempt < attempts) {
                const random = this.options.random ?? Math.random;
                await this.sleep(Math.floor(random() * 300 * 2 ** (attempt - 1)));
            }
        }
        throw lastError instanceof Error
            ? lastError
            : new TRYPORTALError('SCORE_UNREACHABLE', 'the score could not be submitted', {
                retryable: true,
            });
    }
    async createShareCard(input) {
        const response = await this.authenticatedFetch(`${this.options.apiBaseUrl}/v1/game-sessions/${this.options.gameSessionId}/share-cards`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
                authorization: `Bearer ${this.jws}`,
                'idempotency-key': input.scoreSubmissionId,
            },
            body: JSON.stringify({
                scoreSubmissionId: input.scoreSubmissionId,
            }),
        });
        if (!response.ok) {
            throw new TRYPORTALError('SHARE_CARD_FAILED', 'the platform did not create a share card', {
                status: response.status,
                detail: await safeText(response),
            });
        }
        const card = await response.json();
        if (!isRecord(card) || typeof card['shareId'] !== 'string' || typeof card['url'] !== 'string')
            throw new TRYPORTALError('SHARE_CARD_FAILED', 'the platform returned an invalid share card');
        return {
            shareId: card['shareId'],
            shareUrl: card['url'],
            imageUrl: typeof card['imageUrl'] === 'string' ? card['imageUrl'] : null,
        };
    }
    /**
     * Read a page of this session's board straight from the API.
     *
     * Two things about this call are worth stating rather than leaving to be
     * discovered.
     *
     * The competition is the one this transport was constructed with, not one the
     * caller supplies — the same rule the bridge enforces structurally. It grants
     * nothing new either way: `GET /v1/competitions/{id}/leaderboard` is a public,
     * unauthenticated endpoint, so a game that already has network permission
     * could always read any *public* board. What it cannot do, here or anywhere,
     * is change which board its own score is filed under.
     *
     * No `Authorization` header is sent. The endpoint does not read one — it
     * resolves "my row" from the platform session cookie — so attaching the launch
     * token would hand a credential to a route with no use for it. Direct calls
     * omit cookies, so `myEntry` is null and the public board still arrives.
     */
    async leaderboard(query) {
        const competitionId = this.session.competitionId;
        if (competitionId === '') {
            throw new TRYPORTALError('INVALID_USAGE', 'this launch carried no competitionId, so there is no board to read');
        }
        const search = new URLSearchParams({ limit: String(query.limit ?? 20) });
        if (query.cursor !== undefined)
            search.set('cursor', query.cursor);
        let response;
        try {
            response = await this.fetchImpl(`${this.options.apiBaseUrl}/v1/competitions/${encodeURIComponent(competitionId)}` +
                `/leaderboard?${search.toString()}`, { method: 'GET', credentials: 'omit', headers: { accept: 'application/json' } });
        }
        catch (error) {
            throw new TRYPORTALError('LEADERBOARD_UNAVAILABLE', 'the leaderboard could not be reached', {
                retryable: true,
                cause: error,
            });
        }
        if (!response.ok) {
            throw new TRYPORTALError('LEADERBOARD_UNAVAILABLE', 'the platform did not return a board', {
                status: response.status,
                detail: await safeText(response),
                retryable: true,
            });
        }
        return toLeaderboardPage(await response.json(), this.session);
    }
    requestStreamMode(_desired) {
        return Promise.reject(new TRYPORTALError('INVALID_USAGE', 'requestStreamMode() only works inside the hosted play shell'));
    }
    openStreamSettings() {
        return Promise.reject(new TRYPORTALError('INVALID_USAGE', 'openStreamSettings() only works inside the hosted play shell'));
    }
    openCreatorVoice() {
        return Promise.reject(new TRYPORTALError('INVALID_USAGE', 'openCreatorVoice() only works inside the hosted play shell'));
    }
    reportGameOver(reason) {
        if (this.options.rankingTrack === 'STANDARD' ||
            this.options.rankingTrack === 'OFFICIAL_EVENT') {
            if (this.httpEndRequested || this.closed)
                return;
            this.httpEndRequested = true;
            void this.authenticatedFetch(`${this.options.apiBaseUrl}/v1/game-sessions/${this.options.gameSessionId}/end`, {
                method: 'POST',
                headers: {
                    'content-type': 'application/json',
                    authorization: `Bearer ${this.jws}`,
                    'idempotency-key': this.options.newClientInstanceId(),
                },
                body: JSON.stringify({ clientSequence: 0, reason: 'COMPLETED' }),
                keepalive: true,
            })
                .then((response) => {
                if (!response.ok)
                    this.handlers?.log('game end was not accepted', { status: response.status });
            })
                .catch(() => this.handlers?.log('game end could not reach the platform'));
            return;
        }
        try {
            this.socket?.send(JSON.stringify({ schemaVersion: 1, messageType: 'GAME_OVER', reason }));
        }
        catch {
            // Best effort: the run is over either way.
        }
    }
    close(reason) {
        if (this.closed)
            return;
        this.closed = true;
        this.httpConnect?.abort();
        this.socket?.close(1000, reason);
        this.socket = undefined;
        this.setState('CLOSED');
    }
}
async function safeText(response) {
    try {
        return (await response.text()).slice(0, 500);
    }
    catch {
        return '';
    }
}
